import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'
import { ApiV2Error } from '@/lib/api-v2-client'
import { startCheckout } from '@/lib/checkout'
import { isBillingInterval, isPaidPlan, type BillingInterval, type PaidPlan } from '@/lib/plans'
import { canManageBilling, hasPaidPlan } from '@/lib/subscription-access'
import { PlanPicker } from '@/components/plan-picker'

export const Route = createFileRoute('/_authenticated/checkout')({
  validateSearch: (search: Record<string, unknown>): { plan?: PaidPlan; interval?: BillingInterval } => ({
    plan: isPaidPlan(search.plan) ? search.plan : undefined,
    interval: isBillingInterval(search.interval) ? search.interval : undefined,
  }),
  component: CheckoutPage,
  head: () => ({ meta: [{ title: 'Choose your plan — Uptrack' }] }),
})

// Landing page after sign-in from a plan CTA: opens the Paddle overlay at
// once, and stays as a plan picker if the buyer closes it (openspec 111).
function CheckoutPage() {
  const { plan, interval } = Route.useSearch()
  const { organization, user } = useAuth()
  const navigate = useNavigate()
  // A free organization has product access but no subscription: it must still reach checkout.
  const subscribed = hasPaidPlan(organization)
  const opened = useRef(false)
  const mayPay = canManageBilling(user)
  const [opening, setOpening] = useState(!!plan && !subscribed && mayPay)

  useEffect(() => {
    if (subscribed) {
      navigate({ to: '/dashboard' })
      return
    }
    if (!plan || !mayPay || opened.current) return
    opened.current = true
    startCheckout(plan, interval ?? 'annual', { onClose: () => setOpening(false) })
      .catch((err) => {
        toast.error(err instanceof ApiV2Error ? err.message : 'Failed to start checkout')
        setOpening(false)
      })
  }, [plan, interval, subscribed, mayPay, navigate])

  if (subscribed) return null

  return (
    <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 py-10 text-center">
      <h1 className="text-3xl font-semibold">{opening ? 'Opening secure checkout...' : 'Choose your plan'}</h1>
      <p className="text-muted-foreground">
        Every paid plan checks all monitors every 30 seconds. Your account is ready; pick a plan to continue.
      </p>
      <PlanPicker selectedPlan={plan} defaultInterval={interval} />
    </div>
  )
}
